import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import anime from 'animejs'
import delay from 'delay'
import { useTheme, Button, Paper } from '@mui/material'
import Collapse from '@mui/material/Collapse'
import CelebrationIcon from '@mui/icons-material/Celebration'
import CheckBoxIcon from '@mui/icons-material/CheckBox'
import SendIcon from '@mui/icons-material/Send'
import appVersionService, { APP_VERSION, MODAL_VERSION, MAX_MODAL_VIEWS } from '../../services/appVersion'
import Text from '../Text'
import CircularProgressCustom from '../CircularProgressCustom'

export default function NewVersionAppModal() {
  const theme = useTheme()
  const navigate = useNavigate()
  const airplanesRef = useRef()
  const [isLoading, setIsLoading] = useState(true)
  // modalVersion controla SÓ a reabertura do popup de novidades (MODAL_VERSION),
  // que avança só em feature grande — separado da APP_VERSION exibida ao cliente.
  const [modalVersion, setModalVersion] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showContent, setShowContent] = useState(false)

  const getAppVersion = async () => {
    try {
      setModalVersion(MODAL_VERSION)
      setIsLoading(false)
    } catch (error) {
      console.error(error)
      setIsLoading(false)
    }
  }

  const handleShowModal = async () => {
    if(!modalVersion) return
    const savedVersion = appVersionService.getVersionLocalStorage()

    if (savedVersion !== modalVersion) {
      appVersionService.resetModalViewCount()
      appVersionService.saveVersionLocalStorage(modalVersion)
    }

    const viewCount = appVersionService.getModalViewCount()

    if (viewCount < MAX_MODAL_VIEWS) {
      appVersionService.incrementModalViewCount()
      setShowModal(true)
      await delay(700)
      setShowContent(true)
      await delay(200)
      animateAirplanes()
    }
  }

  const handleCloseModal = () => {
    setShowModal(false)
  }

  const animateAirplanes = () => {
    anime({
      targets: airplanesRef.current.children,
      opacity: [0, 1],
      translateX: [-200, 0],
      translateY: [200, 0],
      delay: anime.stagger(100),
      easing: 'easeOutExpo'
    })
  }

  useEffect(() => {
    handleShowModal()
  }, [modalVersion])

  useEffect(() => {
    getAppVersion()
  }, [])

  if(!showModal) return null

  return (
    <div className="fixed w-full h-full top-0 left-0 right-0 z-[9999]">
      <div className="fixed w-full h-full bg-black opacity-[0.5]" />
      <div className="relative w-full h-full flex flex-col items-center overflow-auto max-h-screen py-32 md:py-52">
        <Paper elevation={5} className="relative max-w-[650px] w-full max-h-screen">
          <div ref={airplanesRef} className="absolute w-full">
            <div className="opacity-0 absolute -right-[60px] -top-[20px]">
              <img src="/assets/images/hs-airplane.svg" />
            </div>
            <div className="opacity-0 absolute right-[100px] -top-[10px] w-[70px]">
              <img src="/assets/images/hs-airplane.svg" />
            </div>
            <div className="opacity-0 absolute right-[20px] top-[80px] w-[55px]">
              <img src="/assets/images/hs-airplane.svg" />
            </div>
          </div>
          <div className="flex flex-col pb-20">
            <div className="relative p-6">
              <div
                className={clsx('w-full h-full absolute top-0 left-0 opacity-[0.05]', {
                  'bg-white': theme.isDark,
                  'bg-gray-800': !theme.isDark
                })}
              />
              <div className="relative flex space-x-2">
                <CelebrationIcon style={{ fontSize: 35, color: 'inherit' }} />
                <div>
                  <Text className="text-2xl font-black">
                    Novidades
                  </Text>
                  {
                    isLoading
                    ?
                    <div
                      className={clsx('h-[28px] w-[200px] opacity-[0.1] rounded-[3px]', {
                        'bg-white': theme.isDark,
                        'bg-gray-800': !theme.isDark
                      })}
                    />
                    :
                    <Text className="text-lg">
                      Versao v{APP_VERSION}
                    </Text>
                  }
                </div>
              </div>
            </div>
            <div>
            <Collapse in={!showContent} timeout="auto" unmountOnExit>
              <div className="py-12 flex justify-center">
                <CircularProgressCustom />
              </div>
            </Collapse>
            <Collapse in={showContent} timeout="auto" unmountOnExit>
              <div className="p-6">
                <div className="relative min-h-[150px] max-h-[400px] overflow-auto py-2">
                  <div className="space-y-6">
                    <div>
                      <div className="flex items-center space-x-2 mb-3">
                        <SendIcon style={{ fontSize: 28, color: theme.palette.primary.main }} />
                        <Text className="font-black text-xl">
                          Disparo em massa no Enviar mensagem
                        </Text>
                      </div>
                      <ul className="space-y-2">
                        <li>
                          <Text className="text-lg">
                            <CheckBoxIcon /> Planilha ou números colados, com <span className="font-black">modelo aprovado</span> na API Oficial
                          </Text>
                        </li>
                        <li>
                          <Text className="text-lg">
                            <CheckBoxIcon /> Mostra o <span className="font-black">custo máximo e o saldo</span> antes de disparar
                          </Text>
                        </li>
                        <li>
                          <Text className="text-lg">
                            <CheckBoxIcon /> Roda no servidor: <span className="font-black">pode fechar o navegador</span>
                          </Text>
                        </li>
                      </ul>
                    </div>
                    <div>
                      <Text className="font-black text-lg mb-2">
                        Acompanhe cada disparo
                      </Text>
                      <ul className="space-y-2">
                        <li>
                          <Text className="text-lg">
                            <CheckBoxIcon /> Andamento ao vivo na aba <span className="font-black">Acompanhar</span>, com botão de cancelar
                          </Text>
                        </li>
                        <li>
                          <Text className="text-lg">
                            <CheckBoxIcon /> Saldo acabou no meio? Ele <span className="font-black">pausa e volta sozinho</span> após a recarga
                          </Text>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </Collapse>
            </div>
          </div>
          <div
            className="absolute bottom-0 flex items-center justify-between p-6 w-full"
            style={{
              backgroundColor: theme.palette.background.paper
            }}
          >
            <Button onClick={handleCloseModal}>
              FECHAR
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                handleCloseModal()
                // Mantém api e secret da URL (painel do revendedor), como o LinkWithQuery.
                navigate(`/messages/send${window.location.search}`)
              }}
            >
              FAZER UM DISPARO
            </Button>
          </div>
        </Paper>
      </div>
    </div>
  )
}
